<template>
  <div data-test-id="form-simplified-enrollment-subscription">
    <vf-notification
      v-if="notification.message"
      class="mb-4"
      :type="notification.type"
      @close="notification.message = ''"
    >
      {{ notification.message }}
    </vf-notification>
    <slot name="progress-bar">
      <vf-progress-bar max="3" value="1" />
    </slot>

    <base-form
      ref="formRef"
      class="mt-4 space-y-4"
      :class="formClass"
      :form
      @submit="submit"
    >
      <template v-if="mode !== 'email'">
        <slot name="heading">
          <h2 class="mt-4" :class="brandClasses.subheading">
            {{ $t.mobileNumber }}
          </h2>
        </slot>
        <vf-form-field
          v-slot="{ attrs }"
          :hint="mobileOptInHint ? $t.simplifiedEnrollment.mobileNumberHint : ''"
          name="phone"
          :rule="[validateRequired($t.mobileNumber), validatePhone(form.phoneCode)]"
        >
          <input-phone v-model="form.phone" v-model:code="form.phoneCode" v-bind="attrs" required />
        </vf-form-field>
        <vf-form-field v-slot="{ invalid }" name="smsAccepted" :rule="validateRequired('', $t.validators.requiredCheckbox)">
          <vf-checkbox
            v-model="form.smsAccepted"
            data-test-id="form-simplified-enrollment-subscription-sms-checkbox"
            :invalid
            required
            size="sm"
          >
            <form-legal-disclaimer :content="$t.smsTerms" />
          </vf-checkbox>
        </vf-form-field>
      </template>

      <template v-if="mode !== 'sms'">
        <slot name="above-email" />
        <template v-if="mode === 'email'">
          <input-email v-model="form.email" disabled />
        </template>
        <vf-form-field
          v-slot="{ invalid }"
          name="emailAccepted"
          :rule="emailOptInRequired ? validateRequired('', $t.validators.requiredCheckbox) : undefined"
        >
          <vf-checkbox
            v-model="form.emailAccepted"
            data-test-id="form-simplified-enrollment-subscription-email-checkbox"
            :invalid
            :required="emailOptInRequired"
            size="sm"
          >
            <slot name="email-label">
              <form-legal-disclaimer :content="$t.brandAgreement.newsletter" />
            </slot>
          </vf-checkbox>
        </vf-form-field>
      </template>

      <div class="mt-6 space-y-2">
        <slot name="actions" v-bind="{ form, loading, skip }">
          <vf-button class="w-full" :loading type="submit">
            {{ $t.next }}
          </vf-button>
          <vf-button class="w-full" :disabled="loading" variant="secondary" @click="emit('continue', false)">
            {{ $t.skipForNow }}
          </vf-button>
        </slot>
      </div>
    </base-form>
  </div>
</template>

<script lang="ts" setup>
import type { BaseForm as BaseFormType } from '#components'
import type { FormLocation, MarketingSelection } from '#types/gtm'
import type { BaseNotification } from '#types/notification'

/**
 * mode is optional; default to 'both'
 * - 'both'  -> SMS consent + Email consent
 * - 'sms'   -> SMS consent only
 * - 'email' -> Email consent only
 */
const { emailOptInRequired = true, formLocation, mobileOptInHint = true, mode = 'both' } = defineProps<{
  emailOptInRequired?: boolean
  formClass?: string
  formLocation: FormLocation
  mobileOptInHint?: boolean
  mode?: 'both' | 'sms' | 'email'
}>()

const emit = defineEmits<{
  continue: [submitted: boolean]
}>()

const { brandClasses } = useAppConfig().components.dialog.simplifiedEnrollment
const { validatePhone, validateRequired } = useLocalizedValidators()

const auth = useAuthStore()
const user = useUserStore()
const subscriptions = useSubscriptions()
const timelyDismissal = useTimelyDismissal()
const { $gtm, $t } = useNuxtApp()

const loading = ref(false)
const formRef = ref<InstanceType<typeof BaseFormType>>()

const [storedPhoneCode, storedPhone] = parseE164Phone(subscriptions.smsNumber.value)

const form = reactive({
  phone: storedPhone || '',
  phoneCode: storedPhoneCode || getPhoneCodeByCountryCode(useCountryCode()) || '',
  smsAccepted: false,
  email: user.details?.email,
  emailAccepted: false
})

const notification = reactive<BaseNotification>({ message: '', type: 'info' })

onMounted(() => {
  if (auth.consumerNo) timelyDismissal.set(`se_subscription_${auth.consumerNo}`)
  if (mode === 'both' && !emailOptInRequired) $gtm.push('authForm.onMarketingImpression', 'Registration', 'OTP', 'ecom', formLocation)
})

const submit = async () => {
  try {
    loading.value = true
    notification.message = ''
    if (mode !== 'sms' && form.emailAccepted) {
      await subscriptions.newsletter({ optIn: true })
    }
    if (mode !== 'email') {
      const phone = formatLocalNumberToE164(form.phone, form.phoneCode)
      await user.updateBasicInfo({ phone, phoneCode: form.phoneCode })
      await subscriptions.sms(phone, form.phoneCode, true)
    }
    if (mode === 'both' && !emailOptInRequired) {
      let selection: MarketingSelection = 'n_yes'

      if (form.emailAccepted && form.smsAccepted) selection = 'se_yes'
      else if (form.emailAccepted) selection = 'e_yes'
      else if (form.smsAccepted) selection = 's_yes'

      $gtm.push('authForm.onMarketingAction', 'Registration', 'MarketingOptIn Complete', 'OTP', 'ecom', formLocation, selection)
    }
    emit('continue', true)
  }
  catch (err) {
    assertApiError(err)
    notification.message = err.message
    notification.type = 'error'
  }
  finally {
    loading.value = false
  }
}

const skip = async () => {
  if (form.emailAccepted) {
    try {
      loading.value = true
      notification.message = ''
      await subscriptions.newsletter({ optIn: true })

      $gtm.push('authForm.onMarketingAction', 'Registration', 'MarketingOptIn Complete', 'OTP', 'ecom', formLocation, 'e_no')
    }
    catch (err) {
      assertApiError(err)
      notification.message = err.message
      notification.type = 'error'
    }
    finally {
      loading.value = false
    }
  }
  else {
    $gtm.push('authForm.onMarketingAction', 'Registration', 'MarketingOptIn Complete', 'OTP', 'ecom', formLocation, 'n_no')
  }

  emit('continue', false)
}
</script>
